import { memo, useId, type ReactNode } from "react";
import type { WebhookAttempt } from "@/hooks/use-webhook-event-logs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@/components/ui/table";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { EVENT_LABEL, attemptKey, formatDuration, groupAttempts, outcomeBadge, pretty } from "./webhook-attempts";

const TONE = {
  ok: "border-green-500/40 text-green-600",
  fail: "border-red-500/40 text-red-600",
  muted: "text-muted-foreground",
};

const PRE = "bg-muted/40 rounded p-2 overflow-auto max-h-64 whitespace-pre-wrap break-all";

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Headers({ headers }: { headers: Record<string, string> | null | undefined }) {
  const rows = Object.entries(headers ?? {});
  if (rows.length === 0) return null;
  return (
    <Table className="text-xs">
      <TableBody>
        {rows.map(([name, value]) => (
          <TableRow key={name}>
            <TableHead scope="row" className="w-px h-auto py-1 pl-0 pr-4 font-mono font-normal whitespace-nowrap align-top">
              {name}
            </TableHead>
            <TableCell className="py-1 px-0 font-mono break-all">{String(value)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Copies the exact bytes that went out, not the pretty-printed view.
async function copyBody(body: string) {
  try {
    await navigator.clipboard.writeText(body);
    toast.success("Request body copied");
  } catch {
    toast.error("Couldn't copy the request body");
  }
}

function Attempt({ attempt: a }: { attempt: WebhookAttempt }) {
  const badge = outcomeBadge(a);
  return (
    <AccordionItem value={attemptKey(a)} className="border rounded-lg px-3">
      <AccordionTrigger className="hover:no-underline py-2.5">
        <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-left">
          <span className="text-xs font-mono text-muted-foreground">#{a.attempt_no}</span>
          <Badge variant="outline" className={TONE[badge.tone]}>
            {badge.label}
          </Badge>
          {a.duration_ms != null && <span className="text-xs text-muted-foreground">{formatDuration(a.duration_ms)}</span>}
          {a.source && <span className="text-xs text-muted-foreground">{a.source}</span>}
          <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
            {new Date(a.started_at).toLocaleString()}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-4 pb-2 text-xs">
          {"url" in a ? (
            <>
              <Section
                title="Request"
                action={
                  a.request_body != null && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2"
                      aria-label="Copy request body"
                      onClick={() => copyBody(a.request_body)}
                    >
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  )
                }
              >
                <div className="font-mono break-all">
                  {a.request_method} {a.url}
                </div>
                <Headers headers={a.request_headers} />
                {a.request_body == null ? (
                  <div className="text-muted-foreground italic">Body no longer stored.</div>
                ) : (
                  <pre className={PRE}>{pretty(a.request_body)}</pre>
                )}
              </Section>
              <Section title="Response">
                <div>{a.response_status == null ? "No response" : `Status ${a.response_status}`}</div>
                <Headers headers={a.response_headers} />
                {a.response_body && <pre className={PRE}>{pretty(a.response_body)}</pre>}
                {a.error_message && <div className="text-red-600 break-all">{a.error_message}</div>}
              </Section>
            </>
          ) : (
            <div className="text-muted-foreground italic">
              Sent to {a.url_host ?? "—"}. The full request and response require developer access.
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

/**
 * Every POST of the call's webhooks, grouped by event; each opens to the request and the response.
 * Memoized: an open row pretty-prints its request body (up to 1 MB), which should not be redone
 * every time the tab's retry panel changes.
 */
export const WebhookAttempts = memo(function WebhookAttempts({ attempts }: { attempts: WebhookAttempt[] }) {
  const id = useId();
  return (
    <div className="space-y-4">
      {groupAttempts(attempts).map((group) => (
        <section key={group.event} aria-labelledby={`${id}-${group.event}`} className="space-y-2">
          <div id={`${id}-${group.event}`} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {EVENT_LABEL[group.event] ?? group.event}
          </div>
          <Accordion type="multiple" className="space-y-2">
            {group.attempts.map((a) => (
              <Attempt key={attemptKey(a)} attempt={a} />
            ))}
          </Accordion>
        </section>
      ))}
    </div>
  );
});
